import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { formatPrize } from "@/lib/prize";
import { CheckCircle, Money, ClockCounterClockwise, XCircle, FileText } from "@phosphor-icons/react";
import { Heading } from "./NKDaily";

const TABS = [
  { v: "pending", l: "Pending", Icon: ClockCounterClockwise },
  { v: "approved", l: "Approved", Icon: CheckCircle },
  { v: "paid", l: "Paid", Icon: Money },
  { v: "rejected", l: "Rejected", Icon: XCircle },
];

export default function Rewards() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/payouts/admin/list", { params: { status: tab } });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (r) => {
    const note = prompt("Optional approval note", "");
    try {
      await api.post(`/payouts/admin/${r.payout_id}/approve`, { note: note || null });
      toast.success("Approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (r) => {
    const reason = prompt("Reason for rejection (will be shown to the player):", "");
    if (!reason) return;
    try {
      await api.post(`/payouts/admin/${r.payout_id}/reject`, { reason });
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markPaid = async (r) => {
    const reference = prompt("Payment reference (MoMo confirmation, bank txn id, etc.)", "");
    if (!reference) return;
    const note = prompt("Optional note", "");
    try {
      await api.post(`/payouts/admin/${r.payout_id}/mark-paid`, { reference, note: note || null });
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Heading title="Prize Payouts"
               subtitle="Top-3 finishers per country can claim. You approve and mark paid here. All transitions are logged immutably." />

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`rewards-tab-${t.v}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${
                    tab === t.v ? "bg-nk-accent text-nk-bg border-nk-accent"
                                : "text-nk-mute border-nk-line hover:border-nk-accent hover:text-nk-text"
                  }`}>
            <t.Icon size={12} weight="bold" /> {t.l}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-nk-dim">No {tab} claims.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.payout_id} className="nk-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0">
                  <AvatarIcon id={null} size={32} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-display text-base truncate">{r.user_name}</span>
                      {r.country && <CountryFlag country={r.country} size={12} />}
                      <span className="text-[10px] uppercase tracking-wider text-nk-gold">#{r.rank}</span>
                    </div>
                    <p className="text-xs text-nk-mute font-mono">{r.user_email}</p>
                    <p className="text-[11px] text-nk-dim mt-1">
                      Season {r.season_number} · {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-lg text-nk-gold">{formatPrize({amount: r.prize_amount, currency: r.prize_currency}, r.country)}</p>
                  <p className="text-[11px] text-nk-mute capitalize">{r.payout_method?.replace("_", " ")}</p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <KV k="Holder" v={r.payout_account_name} />
                <KV k={r.payout_method === "mobile_money" ? "MoMo number" : "Account"} v={r.payout_account} mono />
                {r.bank_name && <KV k="Bank" v={r.bank_name} />}
                {r.payout_reference && <KV k="Reference" v={r.payout_reference} mono />}
                {r.notes && <KV k="Notes" v={r.notes} />}
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {r.status === "pending" && (
                  <>
                    <button onClick={() => approve(r)} data-testid={`approve-${r.payout_id}`}
                            className="px-3 py-1.5 bg-nk-accent text-nk-bg text-xs font-medium rounded-sm">Approve</button>
                    <button onClick={() => reject(r)} data-testid={`reject-${r.payout_id}`}
                            className="px-3 py-1.5 border border-nk-bad text-nk-bad text-xs font-medium rounded-sm">Reject</button>
                  </>
                )}
                {r.status === "approved" && (
                  <button onClick={() => markPaid(r)} data-testid={`paid-${r.payout_id}`}
                          className="px-3 py-1.5 bg-nk-gold text-nk-bg text-xs font-medium rounded-sm">Mark as paid</button>
                )}
                <button onClick={() => setDetail(r.payout_id)} data-testid={`audit-${r.payout_id}`}
                        className="px-3 py-1.5 border border-nk-line text-nk-mute text-xs rounded-sm inline-flex items-center gap-1">
                  <FileText size={11} /> Audit log
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && <AuditDrawer payoutId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <p className="text-[10px] text-nk-mute uppercase tracking-wider">{k}</p>
      <p className={`text-nk-text break-words ${mono ? "font-mono" : ""}`}>{v}</p>
    </div>
  );
}

function AuditDrawer({ payoutId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/payouts/admin/${payoutId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [payoutId]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-nk-bg border-l border-nk-line p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-xl font-medium mb-1">Audit log</h2>
        <p className="text-xs text-nk-mute font-mono mb-4">{payoutId}</p>
        {!data ? <p className="text-sm text-nk-dim">Loading…</p> : (
          <ul className="space-y-3" data-testid="audit-log">
            {data.audit.map((a) => (
              <li key={a.audit_id} className="nk-card p-3 text-xs">
                <p>
                  <span className="text-nk-mute capitalize">{a.actor_role}</span>
                  {a.actor_email && <span className="text-nk-dim"> ({a.actor_email})</span>}
                  {" · "}
                  <span className="capitalize text-nk-text">{a.from_status || "—"}</span>
                  {" → "}
                  <span className={`capitalize ${
                    a.to_status === "paid" ? "text-nk-gold" :
                    a.to_status === "rejected" ? "text-nk-bad" :
                    a.to_status === "approved" ? "text-nk-accent" : "text-nk-text"
                  }`}>{a.to_status}</span>
                </p>
                {a.note && <p className="text-nk-mute mt-1">{a.note}</p>}
                <p className="text-[10px] text-nk-dim mt-1 font-mono">{new Date(a.at).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
